import { type FC, useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import { formatDistanceToNow } from 'date-fns';
import {
  IconChevronDown,
  IconChevronUp,
  IconHelpCircle,
  IconLoader2,
  IconMinus,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { ButtonField, Tooltip, TooltipBody } from '@wk/components-react16';

import { normalizeSiteComplianceDivision } from '@/domains/siteCompliance';
import { ORR_PLATFORMS } from '@/config/constants';
import { AutomationPlatform } from '@/domains/evaluation/evaluation.types';

import type { SiteComplianceListRow } from './SiteCompliancePage.types';

const renderEvaluationSource = (platform: string, siteId: string) => {
  const entry =
    ORR_PLATFORMS[platform as AutomationPlatform] ??
    ORR_PLATFORMS[AutomationPlatform.UNKNOWN];
  const Icon = entry.icon;
  const tooltipId = `source-${siteId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <span className="flex items-center">
      <Tooltip position="right" targetSelector={`#${tooltipId}`}>
        <TooltipBody slot="tooltipBody">{entry.label}</TooltipBody>
      </Tooltip>
      <span id={tooltipId} className="flex h-5 w-5 items-center">
        <Icon className="h-full w-full" />
      </span>
    </span>
  );
};

interface SiteComplianceContentProps {
  emptyStateMessage: string | null;
  isMobileViewport: boolean;
  isLoading: boolean;
  sites: SiteComplianceListRow[];
  onRunEvaluation: (site: SiteComplianceListRow) => void;
  onViewSite: (siteId: string) => void;
}

const sanitizeTooltipId = (value: string) =>
  value.replace(/[^a-zA-Z0-9_-]/g, '-');

const formatLastReviewedAt = (lastReviewedAt: string | null) => {
  if (!lastReviewedAt) {
    return 'Not Evaluated';
  }

  return formatDistanceToNow(new Date(lastReviewedAt), {
    addSuffix: true,
  });
};

const SiteComplianceContent: FC<SiteComplianceContentProps> = ({
  emptyStateMessage,
  isMobileViewport,
  isLoading,
  sites,
  onRunEvaluation,
  onViewSite,
}) => {
  const getScoreDirection = (score: number, previousScore: number | null) => {
    if (previousScore === null || previousScore === undefined) {
      return null;
    }

    if (score > previousScore) {
      return <IconChevronUp size={14} className="text-green-600" />;
    }

    if (score < previousScore) {
      return <IconChevronDown size={14} className="text-red-500" />;
    }

    return <IconMinus size={14} className="text-gray-500" />;
  };

  const renderScoreBlock = (
    site: SiteComplianceListRow,
    type: 'last' | 'upcoming',
  ) => {
    const isUpcoming = type === 'upcoming';
    const score = isUpcoming
      ? site.upcomingEvaluationScore
      : site.lastEvaluationScore;
    const checkpointCount = isUpcoming
      ? site.upcomingEvaluationCheckpointCount
      : site.lastEvaluationCheckpointCount;
    const previousScore = isUpcoming
      ? site.previousUpcomingEvaluationScore
      : site.previousEvaluationScore;

    if (!site.evaluationId) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <IconMinus size={14} />
          <span>No evaluations</span>
        </div>
      );
    }

    if (score === null || score === undefined) {
      return <span className="text-sm text-gray-500">N/A</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{score}%</span>
          {getScoreDirection(score, previousScore)}
        </div>
        <span className="text-[10px] text-gray-500">
          {checkpointCount} Checkpoints
        </span>
      </div>
    );
  };

  const columnDefs = useMemo<ColDef<SiteComplianceListRow>[]>(
    () => [
      {
        headerName: 'Site ID',
        field: 'siteId',
        minWidth: 320,
        flex: 2,
      },
      {
        headerName: 'Primary Division',
        field: 'primaryDivision',
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: CustomCellRendererProps<SiteComplianceListRow>) => {
          const site = params.data;
          return normalizeSiteComplianceDivision(
            site?.primaryDivision ?? 'Unknown',
          );
        },
      },
      {
        headerName: 'Source',
        field: 'lastEvaluationPlatform',
        flex: 0.5,
        minWidth: 80,
        sortable: false,
        cellRenderer: (params: CustomCellRendererProps<SiteComplianceListRow>) => {
          const platform = params.data?.lastEvaluationPlatform;
          const siteId = params.data?.siteId;
          if (!platform || !siteId) {
            return null;
          }
          return renderEvaluationSource(platform, siteId);
        },
      },
      {
        field: 'lastReviewedAt',
        headerName: 'Last Evaluated At',
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: CustomCellRendererProps<SiteComplianceListRow>) => {
          const site = params.data;

          if (!site?.lastReviewedAt || !site.evaluationId) {
            return <span>Not Evaluated</span>;
          }

          return <span>{formatLastReviewedAt(site.lastReviewedAt)}</span>;
        },
      },
      {
        headerName: 'Last Evaluation Score',
        field: 'lastEvaluationScore',
        flex: 1,
        minWidth: 150,
        headerComponentParams: {
          innerHeaderComponent: () => {
            return (
              <div className="flex items-center gap-2">
                Last Evaluation Score
                <span
                  id="site-last-evaluation-score-tooltip"
                  className="text-[#005b92]"
                >
                  <IconHelpCircle size={16} />
                </span>
                <Tooltip
                  position="top"
                  targetSelector="#site-last-evaluation-score-tooltip"
                >
                  <TooltipBody slot="tooltipBody">
                    Site compliance score based on published checkpoints
                  </TooltipBody>
                </Tooltip>
              </div>
            );
          },
        },
        cellRenderer: (params: CustomCellRendererProps<SiteComplianceListRow>) =>
          params.data ? renderScoreBlock(params.data, 'last') : null,
      },
      {
        headerName: 'Upcoming Evaluation Score',
        field: 'upcomingEvaluationScore',
        flex: 1,
        minWidth: 150,
        headerComponentParams: {
          innerHeaderComponent: () => {
            return (
              <div className="flex items-center gap-2">
                Upcoming Evaluation Score
                <span
                  id="site-upcoming-evaluation-score-tooltip"
                  className="text-[#005b92]"
                >
                  <IconHelpCircle size={16} />
                </span>
                <Tooltip
                  position="top"
                  targetSelector="#site-upcoming-evaluation-score-tooltip"
                >
                  <TooltipBody slot="tooltipBody">
                    Site compliance score including checkpoints in preview
                  </TooltipBody>
                </Tooltip>
              </div>
            );
          },
        },
        cellRenderer: (params: CustomCellRendererProps<SiteComplianceListRow>) =>
          params.data ? renderScoreBlock(params.data, 'upcoming') : null,
      },
      {
        headerName: 'Actions',
        colId: 'actions',
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: CustomCellRendererProps<SiteComplianceListRow>) => {
          const site = params.data;

          if (!site) {
            return null;
          }

          const sanitizedId = sanitizeTooltipId(site.siteId);
          const runId = `run-evaluation-${sanitizedId}`;

          return (
            <div className="flex h-full w-full items-center justify-center gap-2">
              <button
                type="button"
                className="flex cursor-pointer items-center justify-center"
                aria-label={`Run evaluation for ${site.siteId}`}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onRunEvaluation(site);
                }}
              >
                <IconPlayerPlay id={runId} size={16} />
              </button>
              <Tooltip position="left" targetSelector={`#${runId}`}>
                <TooltipBody slot="tooltipBody">Run evaluation</TooltipBody>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [onRunEvaluation],
  );

  if (emptyStateMessage) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center rounded border border-gray-200 bg-white px-4 text-center text-sm text-gray-500">
        {emptyStateMessage}
      </div>
    );
  }

  if (isMobileViewport) {
    return (
      <>
        <div className="site-compliance-cards-container flex flex-col gap-3">
          {isLoading ? (
            <div className="flex h-full min-h-[220px] items-center justify-center text-gray-500">
              <IconLoader2 size={22} className="animate-spin" />
            </div>
          ) : (
            sites.map((site) => (
              <article
                key={site.siteId}
                className="rounded-lg border bg-white p-3 shadow-sm"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                  <button
                    type="button"
                    onClick={() => onViewSite(site.siteId)}
                    className="flex min-w-0 flex-col items-start text-left"
                  >
                    <span className="block w-full break-words text-base font-semibold leading-5">
                      {site.siteId}
                    </span>
                  </button>
                  <div className="flex items-center justify-end gap-2 self-start">
                    <ButtonField mode="ghost" size="small">
                      <button
                        type="button"
                        aria-label={`Run evaluation for ${site.siteId}`}
                        onClick={() => onRunEvaluation(site)}
                      >
                        <IconPlayerPlay size={14} />
                      </button>
                    </ButtonField>
                  </div>
                </div>

                <dl className="mt-3 grid grid-cols-2 items-start gap-x-3 gap-y-3 text-sm">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                      Primary Division
                    </dt>
                    <dd>{normalizeSiteComplianceDivision(site.primaryDivision ?? 'Unknown')}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                      Last Evaluated At
                    </dt>
                    <dd>
                      {site.lastReviewedAt && site.evaluationId
                        ? formatLastReviewedAt(site.lastReviewedAt)
                        : 'Not Evaluated'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                      Last Score
                    </dt>
                    <dd>{renderScoreBlock(site, 'last')}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                      Upcoming Score
                    </dt>
                    <dd>{renderScoreBlock(site, 'upcoming')}</dd>
                  </div>
                </dl>
              </article>
            ))
          )}
        </div>
        <ol className="ml-auto flex w-full list-inside list-decimal flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:gap-8 lg:text-sm">
          <li>
            Upcoming Score includes site compliance checkpoints that are
            currently in preview
          </li>
          <li>
            Timeframe for when preview checkpoints will become part of the
            published site score is to be decided
          </li>
        </ol>
      </>
    );
  }

  return (
    <>
      <div className="site-compliance-grid-container w-full max-sm:overflow-x-auto sm:flex-1 sm:min-h-0">
        <div className="h-full max-sm:h-[52vh] max-sm:min-h-[320px] max-sm:min-w-[1200px] max-sm:w-full">
          <AgGridReact
            animateRows={false}
            loading={isLoading}
            onCellClicked={(event) => {
              if (event.column.getColId() === 'actions') {
                return;
              }

              if (event.data) {
                onViewSite(event.data.siteId);
              }
            }}
            rowData={sites}
            columnDefs={columnDefs}
          />
        </div>
      </div>
      <ol className="ml-auto flex w-full list-inside list-decimal flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:gap-8 lg:text-sm">
        <li>
          Upcoming Score includes site compliance checkpoints that are
          currently in preview
        </li>
        <li>
          Timeframe for when preview checkpoints will become part of the
          published site score is to be decided
        </li>
      </ol>
    </>
  );
};

export default SiteComplianceContent;
